// ===================================================================
// services/memoryDuelService.ts
// Memory Duel — jeu de memory tour par tour entre deux amis (Supabase
// Realtime). Le plateau est partagé : chaque coup est écrit en base et
// diffusé en direct à l'adversaire via postgres_changes.
//
// 🔒 Anti-triche : depuis la migration backend 2026-10-09_harden_games_and_xp.sql,
// le plateau secret est stocké côté serveur (memory_duel_decks, illisible) et
// tous les coups passent par des fonctions RPC qui valident le tour et
// calculent elles-mêmes paires et scores. Les écritures directes sur
// memory_duels sont refusées.
//
// SQL d'origine (historique — remplacé par la migration ci-dessus) :
//
// CREATE TABLE memory_duels (
//   id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
//   player1_id UUID REFERENCES user_profiles(id) ON DELETE CASCADE,
//   player2_id UUID REFERENCES user_profiles(id) ON DELETE CASCADE,
//   language_code TEXT NOT NULL,
//   cards JSONB NOT NULL,
//   revealed JSONB NOT NULL DEFAULT '[]',
//   matched JSONB NOT NULL DEFAULT '[]',
//   current_turn UUID NOT NULL,
//   scores JSONB NOT NULL DEFAULT '{}',
//   status TEXT NOT NULL DEFAULT 'pending',
//   created_at TIMESTAMPTZ DEFAULT NOW(),
//   updated_at TIMESTAMPTZ DEFAULT NOW()
// );
// ALTER TABLE memory_duels ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "read_own" ON memory_duels FOR SELECT
//   USING (auth.uid() = player1_id OR auth.uid() = player2_id);
// CREATE POLICY "insert_own" ON memory_duels FOR INSERT
//   WITH CHECK (auth.uid() = player1_id);
// CREATE POLICY "update_own" ON memory_duels FOR UPDATE
//   USING (auth.uid() = player1_id OR auth.uid() = player2_id);
// ALTER PUBLICATION supabase_realtime ADD TABLE memory_duels;
// ===================================================================
import { supabase } from './supabaseAudioService';
import { LanguageCode } from '../types/types';
import { VocabEntry, getMemoryPool } from '../utils/vocabExtract';

export type DuelStatus = 'pending' | 'active' | 'completed' | 'declined';

/** Carte publique : seul l'id est connu tant qu'elle n'est pas retournée. */
export interface MemoryCard {
  id: string;
}

/** Face d'une carte, publiée par le serveur quand elle est retournée ou appariée. */
export interface MemoryCardFace {
  id: string;
  kind: 'word' | 'clue';
  text: string;
  illustration?: string;
}

export interface MemoryDuel {
  id: string;
  player1_id: string;
  player2_id: string;
  language_code: LanguageCode;
  cards: MemoryCard[];
  revealed: string[];
  matched: string[];
  current_turn: string;
  scores: Record<string, number>;
  faces: Record<string, MemoryCardFace>;
  status: DuelStatus;
  created_at: string;
  updated_at: string;
  // Enrichi côté client
  player1_username?: string | null;
  player2_username?: string | null;
}

const PAIR_COUNT = 8; // 8 paires = 16 cartes (grille 4x4)

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Vocabulaire du duel ; c'est le serveur qui crée et mélange les cartes. */
function buildPairs(languageCode: LanguageCode): Array<{ word: string; clue: string; illustration?: string }> {
  const pool: VocabEntry[] = shuffle(getMemoryPool(languageCode)).slice(0, PAIR_COUNT);
  return pool.map(entry => ({ word: entry.word, clue: entry.clue, illustration: entry.illustration }));
}

export const createMemoryDuel = async (
  player1Id: string,
  player2Id: string,
  languageCode: LanguageCode,
): Promise<string> => {
  // player1Id = utilisateur connecté (déduit côté serveur via auth.uid())
  void player1Id;
  const { data, error } = await supabase.rpc('create_memory_duel', {
    p_opponent: player2Id,
    p_language: languageCode,
    p_pairs: buildPairs(languageCode),
  });

  if (error) throw error;
  return data as string;
};

export const getMyMemoryDuels = async (userId: string): Promise<MemoryDuel[]> => {
  const { data, error } = await supabase
    .from('memory_duels')
    .select('*')
    .or(`player1_id.eq.${userId},player2_id.eq.${userId}`)
    .neq('status', 'declined')
    .order('updated_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return enrichWithUsernames((data ?? []) as MemoryDuel[]);
};

export const getMemoryDuel = async (id: string): Promise<MemoryDuel | null> => {
  const { data, error } = await supabase.from('memory_duels').select('*').eq('id', id).single();
  if (error) return null;
  return data as MemoryDuel;
};

export const acceptMemoryDuel = async (id: string): Promise<void> => {
  const { error } = await supabase.rpc('respond_memory_duel', { p_duel: id, p_accept: true });
  if (error) throw error;
};

export const declineMemoryDuel = async (id: string): Promise<void> => {
  const { error } = await supabase.rpc('respond_memory_duel', { p_duel: id, p_accept: false });
  if (error) throw error;
};

/**
 * Retourne une carte. Le serveur vérifie le tour et publie la face de la
 * carte (diffusée en direct à l'adversaire) ; retourne la partie à jour.
 */
export const revealCard = async (duelId: string, cardId: string): Promise<MemoryDuel> => {
  const { data, error } = await supabase.rpc('reveal_memory_card', { p_duel: duelId, p_card: cardId });
  if (error) throw error;
  return data as MemoryDuel;
};

/**
 * Résout le coup une fois les 2 cartes visibles (appelé par le joueur actif
 * après un court délai, pour laisser le temps aux deux joueurs de les voir).
 * Le serveur détermine la paire : trouvée → le joueur rejoue ; sinon les
 * cartes sont re-masquées et le tour passe à l'adversaire.
 */
export const resolveTurn = async (duelId: string): Promise<MemoryDuel> => {
  const { data, error } = await supabase.rpc('resolve_memory_turn', { p_duel: duelId });
  if (error) throw error;
  return data as MemoryDuel;
};

async function enrichWithUsernames(duels: MemoryDuel[]): Promise<MemoryDuel[]> {
  if (duels.length === 0) return duels;
  const ids = [...new Set(duels.flatMap(d => [d.player1_id, d.player2_id]))];
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, username, display_name')
    .in('id', ids);
  const map = new Map((profiles ?? []).map((p: any) => [p.id, p]));
  duels.forEach(d => {
    const p1 = map.get(d.player1_id) as any;
    const p2 = map.get(d.player2_id) as any;
    d.player1_username = p1?.display_name || p1?.username || 'Joueur';
    d.player2_username = p2?.display_name || p2?.username || 'Joueur';
  });
  return duels;
}
